// =========================================================================
// Asset360 projections. Fork-owned (zone C).
//
// Pure functions only: no React, no fetch, no clock, no engine mutation. The
// engine (`shared/oem-engine.ts`) owns every figure that was a figure in the
// demo; this file owns the *arrangement* of those figures into the shapes the
// landing page renders, plus the two derivations the executive hero needs
// that the demo never had a surface for.
//
// Why the hero is here and not in the engine: the demo has no hero. Jay's
// brief (plan §Context, 22 Sep) is to lead with the intelligence-layer story
// — full cost visibility across siloed systems, then predictive maintenance
// ahead of the curve — and those two statements are a *composition* of
// existing engine outputs for one surface. That is the definition of a
// transformation (CLAUDE.md, 4-layer §3). No new arithmetic is invented: each
// number below traces to a named engine export.
//
// Transformation layer (layer 3).
// @see docs/logic/asset360/overview.md
// =========================================================================

import {
  BY_ID,
  FLEET,
  MINES,
  MINE_ORDER,
  type Mine,
} from '../../data/core';
import {
  AVAIL_FROM,
  AVAIL_TO,
  avgAvail,
  correctiveSharePct,
  costToServeBySite,
  FLEET_RULES,
  ctKPIs,
  fmt$,
  fmtk,
  groupNum,
  meanCostPerOpHour,
  miningLabel,
  renewals,
  statusOf,
  totalCostOfInaction,
  type BulletinCtx,
  type CaseCtx,
  type ContractCtx,
  type ControlTowerKpi,
  type CssCtx,
  type InventoryCtx,
  type MaximoCtx,
  type OemJob,
  type OrderCtx,
  type PartsCatalogueCtx,
  type ShiftCtx,
  type TechJobCtx,
  type TechPresenceCtx,
  type TimesheetCtx,
  type TrainingCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';
import { ALERTS } from '../../shared/seed/alerts';
import { INCIDENTS } from '../../shared/seed/incidents';
import { CONNECTORS, type OemConnector } from '../../shared/seed/connectors';
import { AGENTS, type OemAgent } from '../../shared/seed/agents';
import { LOOPS, type OemLoop } from '../../shared/seed/loops';
import {
  AGENT_COLOURS,
  MODULES,
  type ModuleId,
  type OemModule,
} from '../../shared/seed/modules';
import type { OemAction } from '../../shared/seed/action';
import type { MineCode } from '../../schema/machine';
import { SYSTEM_FAMILIES } from '../help/PlatformMapScene.transform';

/** Bands that put a machine on the watch list — the demo's `ATTN`. */
const ATTENTION: readonly string[] = ['poor', 'vpoor', 'major'];

/** Bands the demo calls "very poor or worse". */
const SEVERE: readonly string[] = ['vpoor', 'major'];

/** The dataset's own "as of" stamp, printed under the hero. Demo line 2990. */
export const AS_OF = 'as of 09 Sep 2026 06:05';

// ---------------------------------------------------------------------------
// Hero — the two outcomes
// ---------------------------------------------------------------------------

/** One half of the hero: a claim, the figure that proves it, and the proof. */
export interface HeroOutcome {
  /** Short eyebrow naming the capability. */
  readonly kicker: string;
  /** The claim, as a sentence an executive would say out loud. */
  readonly claim: string;
  /** The headline figure, already formatted. */
  readonly figure: string;
  /** What the figure is. */
  readonly unit: string;
  /** Two or three supporting clauses, each already interpolated. */
  readonly support: readonly string[];
  /** Where "see it" goes. */
  readonly action: OemAction;
  /** Label on the link. */
  readonly actionLabel: string;
}

/** Everything the hero needs, in one call. */
export interface Hero {
  readonly outcomes: readonly HeroOutcome[];
  /** The platform line: modules · loops · agents · systems. */
  readonly platformLine: string;
  /** Open-job count plus the as-of stamp. */
  readonly statusLine: string;
}

/**
 * The hero's two claims.
 *
 * **Claim one — cost visibility.** The figure is the whole 18-month ledger
 * summed over {@link costToServeBySite}, which is the only place in the
 * product where every site's spend is priced on one basis. The point of the
 * claim is that this number does not exist in any one of the customer's
 * systems: it is assembled from Maximo, SAP PM, Pitram and the ledger, which
 * is why the connector count sits beside it.
 *
 * **Claim two — predictive maintenance.** The figure is
 * {@link totalCostOfInaction}: across the open alerts, what waiting costs
 * over what acting costs, priced from the rate card rather than estimated.
 * It is the single number that makes the case, and it is the demo's own.
 */
export function hero(ctx: WorkOrderCtx & ShiftCtx, openJobs: number): Hero {
  const sites = costToServeBySite(ctx);
  const ledger = sites.reduce((a, s) => a + s.total, 0);
  const availability = avgAvail(ctx);
  const corrective = correctiveSharePct(ctx);
  const inaction = totalCostOfInaction();
  const watch = FLEET.filter((f) => ATTENTION.includes(statusOf(f.machine_id))).length;

  return {
    outcomes: [
      {
        kicker: 'Cost visibility',
        claim: 'Every dollar of running a mixed fleet, on one basis, across systems that never talk to each other.',
        figure: fmtk(ledger),
        unit: `cost to serve · ${MINE_ORDER.length} sites · 18 months`,
        support: [
          `${CONNECTORS.length} connected systems reconciled to one ledger — Maximo, SAP PM, Pitram, RMS, the lab and the roster`,
          `${fmt$(meanCostPerOpHour())} mean cost per operating hour, priced per machine and per class`,
          `${corrective}% of work orders are corrective — the share the operating model is built to move`,
        ],
        action: { view: 'costs' },
        actionLabel: 'Open the cost summary',
      },
      {
        kicker: 'Predictive maintenance',
        claim: 'Failures priced and caught inside their window, before the machine stops and the shift is lost.',
        figure: fmtk(inaction),
        unit: 'cost of waiting on today’s open alerts',
        support: [
          `${ALERTS.length} open alerts and ${INCIDENTS.length} replayed incidents, each with a committed fix window`,
          `${availability}% fleet availability this week against ${watch} machines on the watch list`,
          'Every figure opens the rows it came from — no number on this page is typed in',
        ],
        action: { view: 'section', sub: 'issues' },
        actionLabel: 'Open live issues',
      },
    ],
    platformLine: `${MODULES.length} modules · ${LOOPS.length} loops · ${AGENTS.length} agents · ${CONNECTORS.length} systems`,
    statusLine: `${groupNum(openJobs)} open jobs · ${AS_OF}`,
  };
}

// ---------------------------------------------------------------------------
// Control tower
// ---------------------------------------------------------------------------

/** Where the per-machine Maximo pack is, as far as the control tower cares. */
export type MaximoLoad = 'loading' | 'ready' | 'unavailable';

/** The eight control-tower tiles, with the Maximo caveat applied. */
export interface ControlTower {
  readonly tiles: readonly ControlTowerKpi[];
  /** Whether the "Contracts & commercial" tile carries its two Maximo counts. */
  readonly maximo: MaximoLoad;
}

/** The one tile whose detail line reads per-machine Maximo records. */
const MAXIMO_TILE = 'Contracts & commercial';

/**
 * The machines whose Maximo record the control tower reads — all of them.
 *
 * The upgrade clause alone could be narrowed to the attention bands, the way
 * `/agents` narrows its set (`maximoMachinesNeeded()`), because
 * {@link upgradeCandidates} discards any machine outside them. The warranty
 * clause cannot: {@link warrantyActive} asks every machine whether its
 * factory warranty is still `Active`, and a machine in `good` health is
 * exactly the one most likely to be. So the tower loads the whole pack, and
 * it loads it only when the tower is on screen (see the scene).
 */
export const towerMaximoIds = (): string[] => FLEET.map((f) => f.machine_id);

/** True once every machine in {@link towerMaximoIds} has a record in the map. */
export const maximoComplete = (ctx: MaximoCtx): boolean =>
  FLEET.every((f) => ctx.maximo[f.machine_id] !== undefined);

/**
 * The control tower, with the Maximo clauses gated on a complete pack.
 *
 * {@link ctKPIs} takes a {@link MaximoCtx} — a map of all 100 per-machine
 * Maximo records — and uses it for two clauses on the "Contracts &
 * commercial" tile: {@link upgradeCandidates} (call-off machines in an
 * attention band) and {@link warrantyActive} (machines still inside factory
 * warranty). Both are the demo's own functions, ported verbatim (demo lines
 * 2826 and 2830), and the tile prints them exactly as `ctKPIs()` writes them.
 * On the 09 Sep 2026 pack that is `9 upgrade candidates · 3 under warranty`.
 *
 * What this function adds is the gate. A map with records missing would not
 * fail: it would print a smaller count, or `0 upgrade candidates · 0 under
 * warranty` for an empty map — a *wrong number rendered as a fact*. So until
 * every record is in, the tile's detail is rebuilt from the clause that does
 * not need Maximo — the contract renewal, from {@link renewals} — and the two
 * Maximo clauses are replaced by a line that says what is happening:
 *
 * - `loading` — the pack is on its way; the line says the counts are coming.
 * - `unavailable` — a record failed to load; the counts are omitted and the
 *   line points at the commercial module rather than printing a partial sum.
 *
 * The figures are a pure function of the frozen pack, so they are the same on
 * every load.
 */
export function controlTower(
  ctx: WorkOrderCtx &
    TechJobCtx &
    ShiftCtx &
    CssCtx &
    ContractCtx &
    MaximoCtx &
    InventoryCtx &
    OrderCtx &
    PartsCatalogueCtx &
    TechPresenceCtx &
    TimesheetCtx &
    TrainingCtx &
    BulletinCtx &
    CaseCtx,
  loading: boolean,
): ControlTower {
  const maximo: MaximoLoad = maximoComplete(ctx) ? 'ready' : loading ? 'loading' : 'unavailable';
  const tiles = ctKPIs(ctx).map((tile) => {
    if (maximo === 'ready' || tile.label !== MAXIMO_TILE) return tile;
    const next = renewals(ctx)[0];
    const tail =
      maximo === 'loading'
        ? 'counting upgrade candidates and warranty cover…'
        : 'warranty and upgrade positions on the commercial module';
    return { ...tile, detail: `${next.customer} renews in ${next.days} days · ${tail}` };
  });
  return { tiles, maximo };
}

// ---------------------------------------------------------------------------
// Module tiles
// ---------------------------------------------------------------------------

/** One of the ten module tiles. */
export interface ModuleTile {
  readonly id: ModuleId;
  readonly n: number;
  readonly name: string;
  readonly short: string;
  readonly agent: string;
  readonly tag: string;
  /** Module swatch, from the seed. */
  readonly color: string;
  /** The seed's glyph markup, rendered through `SeedGlyph` (never injected). */
  readonly icon: string;
  /** First five "what this module does" rows. */
  readonly items: readonly string[];
  /** How many of the remaining rows were not shown. */
  readonly more: number;
  /** Open jobs sitting at this module. */
  readonly openJobs: number;
  /** Up to three machine ids on those jobs, for the stage row. */
  readonly sample: readonly string[];
  /** The card's job line — `'4 open jobs'`, or `null` for the demo's "nothing waiting". */
  readonly jobsLine: string | null;
  /** The module page. */
  readonly action: OemAction;
}

/**
 * The ten module tiles, in poster order, each carrying its live job count.
 *
 * The demo renders `m.items().slice(0,5)` plus a `+N more` row; both are kept.
 * The `figure` template on each item is *not* rendered here — its `expr`
 * segments are opaque source strings by design (`seed/modules.ts`), and the
 * tile has no room for them. The module page is where they belong.
 */
export function moduleTiles(jobs: readonly OemJob[]): ModuleTile[] {
  return MODULES.map((m: OemModule) => {
    const mine = jobs.filter((j) => j.stage === m.id);
    return {
      id: m.id,
      n: m.n,
      name: m.name,
      short: m.short,
      agent: m.agent,
      tag: m.tag,
      color: m.col,
      icon: m.icon,
      items: m.items.slice(0, 5).map((i) => i.what),
      more: Math.max(0, m.items.length - 5),
      openJobs: mine.length,
      sample: mine.slice(0, 3).map((j) => j.machine ?? j.id),
      jobsLine: mine.length ? `${mine.length} open job${mine.length === 1 ? '' : 's'}` : null,
      action: { view: 'module', sub: m.id },
    };
  });
}

/** Widest module job count, for scaling the "where the jobs are" bars. */
export const peakModuleJobs = (tiles: readonly ModuleTile[]): number =>
  Math.max(1, ...tiles.map((t) => t.openJobs));

// ---------------------------------------------------------------------------
// Loop tiles
// ---------------------------------------------------------------------------

/** One of the five feedback-loop tiles. */
export interface LoopTile {
  readonly id: string;
  readonly n: number;
  readonly name: string;
  readonly agent: string;
  readonly color: string;
  /** First sentence of the loop's purpose — the tile has room for one. */
  readonly purpose: string;
  /** First six item rows, as the demo slices them. */
  readonly items: readonly string[];
  /** The loop page. */
  readonly action: OemAction;
}

/** The five loops, in demo order. */
export const loopTiles = (): LoopTile[] =>
  LOOPS.map((l: OemLoop) => ({
    id: l.id,
    n: l.n,
    name: l.name,
    agent: l.agent,
    color: l.col,
    purpose: `${l.purpose.split('. ')[0].replace(/[.\s]+$/, '')}.`,
    items: l.items.slice(0, 6).map((i) => i.what),
    action: { view: 'loops', sub: l.id },
  }));

/**
 * The line under the loop cards. Demo line 2990's `loopnote`: how many of the
 * standing fleet rules came out of single jobs, and where they are kept.
 */
export const fleetRulesLine = (): { count: number; action: OemAction } => ({
  count: FLEET_RULES.length,
  action: { view: 'loops', sub: 'learning' },
});

// ---------------------------------------------------------------------------
// Outcome cards
// ---------------------------------------------------------------------------

/** One of the four "what the operating model is for" cards. */
export interface OutcomeCard {
  readonly title: string;
  readonly detail: string;
}

/**
 * The four outcome cards. Demo line 2990's `out`.
 *
 * The two priced comparisons in the cost card are the demo's own literals —
 * they name two specific closed incidents (S-05 and L-18) and their planned
 * counterfactuals, which live in `INC_PLANNED`, not in a table this page
 * loads. Kept verbatim, as the demo wrote them.
 */
export function outcomeCards(ctx: ShiftCtx & CssCtx & ContractCtx): OutcomeCard[] {
  const availability = avgAvail(ctx);
  const css = ctx.css.map((r) => r.Overall);
  const cssAvg = (css.reduce((a, b) => a + b, 0) / css.length).toFixed(1);
  const battery = FLEET.filter((f) => f.battery).length;
  // Demo line 2990: `SD.filter(s => s.date >= '2026-08-31' && s.date <= '2026-09-06')`.
  // Summed over the SHIFT table and the availability week, not over
  // `telemetry.energy` — that table covers twelve days, and totalling it here
  // would inflate a figure the card labels "this week" by roughly two thirds.
  // The window bounds are the engine's `AVAIL_FROM` / `AVAIL_TO`.
  const co2t =
    ctx.shiftDaily
      .filter((s) => s.date >= AVAIL_FROM && s.date <= AVAIL_TO)
      .reduce((a, s) => a + s.co2_kg, 0) / 1000;

  return [
    {
      title: 'Higher availability',
      detail: `${availability}% this week · ${ALERTS.length} failures caught inside their window`,
    },
    {
      title: 'Lower total cost',
      detail:
        'A$4.6k planned vs A$24.3k as it happened (S-05) · A$4.1k vs A$98.9k (L-18)',
    },
    {
      title: 'Stronger customer partnerships',
      detail: `${cssAvg}/5 customer satisfaction · ${ctx.contracts.length} contracts, 100% of the base covered`,
    },
    {
      title: 'More sustainable mining',
      detail: `${co2t.toFixed(0)} t CO₂-e this week · ${battery} battery-electric loaders on the hydro grid`,
    },
  ];
}

// ---------------------------------------------------------------------------
// Site cards
// ---------------------------------------------------------------------------

/** One of the six mine-site cards. */
export interface SiteCard {
  readonly code: MineCode;
  readonly name: string;
  readonly state: string;
  readonly mining: string;
  readonly machines: number;
  /** Machines in `vpoor` or `major`. */
  readonly severe: number;
  readonly openJobs: number;
  /** `true` when the site streams live rather than at shift end. */
  readonly live: boolean;
  readonly dataMode: string;
}

/** The six sites, in `MINE_ORDER`. Demo line 2990's `sites` strip. */
export function siteCards(jobs: readonly OemJob[]): SiteCard[] {
  return MINE_ORDER.map((code) => {
    const mine: Mine = MINES[code];
    const machines = FLEET.filter((f) => f.mine === code);
    return {
      code,
      name: mine.name,
      state: mine.state,
      mining: miningLabel(mine),
      machines: machines.length,
      severe: machines.filter((f) => SEVERE.includes(statusOf(f.machine_id))).length,
      openJobs: jobs.filter((j) => j.mine === code).length,
      live: mine.data_mode === 'Live',
      dataMode: mine.data_mode,
    };
  });
}

// ---------------------------------------------------------------------------
// Connector and agent strips
// ---------------------------------------------------------------------------

/** A connector chip, with its stat templates dropped. */
export interface ConnectorChip {
  readonly name: string;
  readonly subtitle: string;
  readonly tone: string;
  readonly status: string;
}

/**
 * The connector strip.
 *
 * `subtitle` is trimmed of its trailing parenthetical: the full form runs to
 * a dozen words and the chip is one line. The whole subtitle survives on
 * `/connectors`, so nothing is lost, only deferred.
 */
export const connectorChips = (): ConnectorChip[] =>
  CONNECTORS.map((c: OemConnector) => ({
    name: c.name,
    subtitle: c.subtitle.replace(/\s*\([^)]*\)\s*$/, ''),
    tone: c.tone,
    status: c.status,
  }));

/** One of the ten system-family chips on the Connectors section. */
export interface SystemChip {
  readonly name: string;
  /** What the family holds, e.g. "machine data". */
  readonly what: string;
  /** The connected systems in the family, joined for one line. */
  readonly systems: string;
  readonly action: OemAction;
}

/**
 * The Connectors section's chips. Demo line 2990's `sysl`, which draws
 * `sysFam` (line 2842) — ten families, not the twenty-one connectors: the
 * section argues "one foundation across every kind of system", and the
 * family is the unit of that argument. The per-connector list is
 * `/connectors`, which the section header links to.
 *
 * `SYSTEM_FAMILIES` is the platform map's port of `sysFam`; the landing page
 * reads the same list rather than keeping a second copy. The demo rewrote
 * `' · '` inside a system name to `' — '` so it would not read as a
 * separator in the joined line; kept.
 */
export const systemChips = (): SystemChip[] =>
  SYSTEM_FAMILIES.map((family) => ({
    name: family.name,
    what: family.what,
    systems: family.systems.map((x) => x.replace(' · ', ' — ')).join(' · '),
    action: { view: family.view[0], sub: family.view[1] },
  }));

/** An agent chip. */
export interface AgentChip {
  readonly name: string;
  readonly level: string;
  readonly module: ModuleId;
  /** First sentence of what it does. */
  readonly gist: string;
  /** The agent's swatch — the demo's `AGENT_COL`, accent when unlisted. */
  readonly color: string;
  /** The roster, opened on this agent. */
  readonly action: OemAction;
}

/** The agent roster strip, in demo order. */
export const agentChips = (): AgentChip[] =>
  AGENTS.map((a: OemAgent) => ({
    name: a.name,
    level: a.level,
    module: a.module,
    gist: `${a.description.split('. ')[0].replace(/[.\s]+$/, '')}.`,
    color: AGENT_COLOURS[a.name] ?? 'var(--accent)',
    action: { view: 'agents', sub: a.name },
  }));

// ---------------------------------------------------------------------------
// "How this works" band
// ---------------------------------------------------------------------------

/** One of the four cards that stand in for the intelligence-layer sections. */
export interface HowThisWorksCard {
  readonly title: string;
  /** The figure, e.g. "10". Counted from the data, never typed in. */
  readonly count: number;
  /** What the figure counts, e.g. "modules". */
  readonly noun: string;
  readonly detail: string;
  readonly route: string;
  readonly linkLabel: string;
}

/**
 * The four summary cards that replace the Workflows, Feedback loops,
 * Connectors and AI-agent sections on the landing page (simplification pass
 * B1).
 *
 * The intelligence-layer story is the answer to "why not Power BI on
 * steroids?", so it cannot leave the page — but it was four full sections and
 * roughly four screens of cards, arguing with the hero above it. Four cards
 * carrying the same four counts make the same claim in one band, and each one
 * opens the section it summarises.
 *
 * Every count is `.length` of a list already derived for this page, so the
 * band cannot drift from the surfaces it links to.
 */
export function howThisWorks(counts: {
  modules: number;
  loops: number;
  connectors: number;
  agents: number;
}): HowThisWorksCard[] {
  return [
    {
      title: 'Workflows',
      count: counts.modules,
      noun: 'modules',
      detail: 'Every machine and every job, from the data to the action to the value.',
      route: '/workflows',
      linkLabel: 'All modules',
    },
    {
      title: 'Feedback loops',
      count: counts.loops,
      noun: 'loops',
      detail: 'The outcome of every job goes back into the front of the model.',
      route: '/loops',
      linkLabel: 'All loops',
    },
    {
      title: 'Connectors',
      count: counts.connectors,
      noun: 'systems',
      detail: 'One data foundation across OEM, customer and third-party systems.',
      route: '/connectors',
      linkLabel: 'Integration',
    },
    {
      title: 'AI agents',
      count: counts.agents,
      noun: 'specialists',
      detail: 'Specialist agents working together, drafting and pricing for a person to decide.',
      route: '/agents',
      linkLabel: 'Agent roster',
    },
  ];
}

// ---------------------------------------------------------------------------
// Source payloads
// ---------------------------------------------------------------------------

/**
 * The source rows behind the hero's cost figure — one line per site, priced
 * on the one basis that makes the number comparable.
 *
 * Every headline figure on this page opens the rows it came from; this is the
 * page's own promise and the reason the drawer exists (plan §C).
 */
export function ledgerSource(ctx: WorkOrderCtx): {
  kind: 'table';
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  provenance: string;
} {
  const sites = costToServeBySite(ctx);
  return {
    kind: 'table',
    title: 'Cost to serve by site · 18 months',
    columns: ['Site', 'Machines', 'Ledger A$', 'A$ / op h', 'Labour A$/h', 'Corrective %'],
    rows: sites.map((s) => [
      MINES[s.mine].name,
      s.machines,
      Math.round(s.total),
      s.costPerOpHour,
      s.labourRate,
      s.correctivePct,
    ]),
    provenance: `SAP PM + ledger · ${MINE_ORDER.length} sites · ${groupNum(ctx.workOrders.length)} work orders · extracted 09 Sep 2026`,
  };
}

/** The source rows behind the hero's cost-of-inaction figure. */
export function inactionSource(): {
  kind: 'table';
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  provenance: string;
} {
  return {
    kind: 'table',
    title: 'Cost of waiting · open alerts',
    columns: ['Alert', 'Machine', 'Site', 'Severity', 'Raised'],
    rows: ALERTS.map((a) => [
      a.id,
      a.machine,
      MINES[BY_ID[a.machine].mine].name,
      a.sev === 'crit' ? 'Critical' : 'Warning',
      a.raised,
    ]),
    provenance: `RMS · live and shift-end alert stream · ${ALERTS.length} open · priced against the site rate card`,
  };
}

// ---------------------------------------------------------------------------
// Today panel — resolving an `audit` action to a source slice
// ---------------------------------------------------------------------------

/** The four datasets a persona's Today list can audit. Demo line 2098. */
export type AuditDataset = 'cases' | 'inv' | 'parts' | 'wos';

/** The rows an {@link auditSlice} call can search. */
export interface AuditSources {
  readonly cases: readonly Record<string, unknown>[];
  readonly inv: readonly Record<string, unknown>[];
  readonly parts: readonly Record<string, unknown>[];
  readonly wos: readonly Record<string, unknown>[];
}

/** How many rows a Today audit puts in the drawer before it stops. */
const AUDIT_ROW_LIMIT = 60;

/** How many columns fit the drawer without a horizontal scroll. */
const AUDIT_COL_LIMIT = 8;

const DATASET_LABEL: Readonly<Record<AuditDataset, string>> = {
  cases: 'Salesforce · service cases',
  inv: 'SAP MM · inventory lines',
  parts: 'CatBase · parts catalogue',
  wos: 'SAP PM · work orders',
};

/**
 * Column order per dataset — the identifying field first, then what the
 * filter was about.
 *
 * Without this the columns come out in whatever order the extractor happened
 * to write the keys, which for the inventory table puts `location` first and
 * the part number fourth. A reader scanning a stock list is looking for the
 * part, so the part goes in column one. Fields not named here follow in key
 * order, so a table gaining a column still shows it.
 */
const DATASET_COLUMNS: Readonly<Record<AuditDataset, readonly string[]>> = {
  cases: ['case_no', 'type', 'status', 'machine_id', 'mine', 'opened', 'summary'],
  inv: ['part_no', 'description', 'location', 'qty_on_hand', 'min_level'],
  parts: ['part_no', 'description', 'criticality', 'unit_price_aud', 'lead_time_days'],
  wos: ['wo_id', 'machine_id', 'mine', 'wo_type', 'status', 'created', 'description'],
};

/** Human title for a filtered slice. */
const AUDIT_TITLE: Readonly<Record<AuditDataset, string>> = {
  cases: 'Service case',
  inv: 'Stock below minimum · site stores',
  parts: 'Parts catalogue',
  wos: 'Work orders',
};

/**
 * Order a row's keys: the dataset's preferred columns that actually exist,
 * then everything else in its own order, capped.
 */
function orderColumns(dataset: AuditDataset, keys: readonly string[]): string[] {
  const preferred = DATASET_COLUMNS[dataset].filter((c) => keys.includes(c));
  const rest = keys.filter((k) => !preferred.includes(k));
  return [...preferred, ...rest].slice(0, AUDIT_COL_LIMIT);
}

/**
 * The rows behind a Today item, as a source-drawer payload.
 *
 * The demo's `audit(table, filter)` took an object of equality clauses, with
 * exactly one exception: `audit('inv',{qty_on_hand:(v,r)=>v<r.min_level})`
 * passed a predicate. `seed/action.ts` carries that as
 * `predicate: 'below-min'` rather than as a function, because a function
 * cannot be a seed value; this is where the two forms come back together.
 *
 * Columns are taken from the first matching row and capped: these tables run
 * to twenty-odd fields and a drawer that scrolls sideways is a drawer nobody
 * reads. The provenance line always states the true match count, so a capped
 * view never reads as a complete one.
 */
export function auditSlice(
  dataset: string,
  filter: Readonly<Record<string, string>>,
  sources: AuditSources,
  predicate?: 'below-min',
): {
  kind: 'table';
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  provenance: string;
} {
  const key = (dataset in sources ? dataset : 'wos') as AuditDataset;
  const all = sources[key];

  const matches = all.filter((row) => {
    for (const [field, want] of Object.entries(filter)) {
      if (String(row[field] ?? '') !== want) return false;
    }
    if (predicate === 'below-min') {
      return Number(row.qty_on_hand) < Number(row.min_level) &&
        String(row.location ?? '').includes('site');
    }
    return true;
  });

  const columns = matches.length ? orderColumns(key, Object.keys(matches[0])) : ['no rows'];

  const clauses = Object.entries(filter).map(([k, v]) => `${k} = ${v}`);

  return {
    kind: 'table',
    title: clauses.length ? `${AUDIT_TITLE[key]} · ${clauses.join(' · ')}` : AUDIT_TITLE[key],
    columns,
    rows: matches
      .slice(0, AUDIT_ROW_LIMIT)
      .map((row) => columns.map((c) => normaliseCell(row[c]))),
    provenance: `${DATASET_LABEL[key]} · ${matches.length} of ${all.length} rows${
      matches.length > AUDIT_ROW_LIMIT ? `, first ${AUDIT_ROW_LIMIT} shown` : ''
    } · extracted 09 Sep 2026`,
  };
}

/** Flatten one cell to something the source drawer can print. */
function normaliseCell(value: unknown): string | number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' || typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}
